import { injectable, inject } from "tsyringe";

// shared
import type { IDateAdapter } from "@/shared/libs/date/interface";

// entities
import { AiTutorSessionDto } from "@/entities/ai-tutor-session/domain/dtos";
import type { CurriculumDto, MiddleUnitDto } from "@/entities/curriculum/domain/dtos";

// pages
import type { SessionItemProps } from "@/pages/ai-tutor/models/interface";

import {
    StudySessionNameStrategy,
    StudyReviewSessionNameStrategy,
    ChallengeSessionNameStrategy,
    ConceptEnhancementSessionNameStrategy,
    BaseSessionNameStrategy,
} from "@/pages/ai-tutor/models/converters/SessionNameStrategy";

export interface SessionNameConverterStructure {
    convert: (
        session: AiTutorSessionDto,
        curriculumDtos: CurriculumDto[],
        middleUnitDtos: MiddleUnitDto[],
    ) => SessionItemProps;
}

@injectable()
export class SessionNameConverter implements SessionNameConverterStructure {
    private readonly sessionNameStrategies: BaseSessionNameStrategy<any>[];
    constructor(
        @inject("DateAdapter") private readonly dateAdapter: IDateAdapter,
        @inject(StudySessionNameStrategy)
        private readonly studySessionNameStrategy: StudySessionNameStrategy,
        @inject(StudyReviewSessionNameStrategy)
        private readonly studyReviewSessionNameStrategy: StudyReviewSessionNameStrategy,
        @inject(ChallengeSessionNameStrategy)
        private readonly challengeSessionNameStrategy: ChallengeSessionNameStrategy,
        @inject(ConceptEnhancementSessionNameStrategy)
        private readonly conceptEnhancementSessionNameStrategy: ConceptEnhancementSessionNameStrategy,
    ) {
        this.sessionNameStrategies = [
            this.studySessionNameStrategy,
            this.studyReviewSessionNameStrategy,
            this.challengeSessionNameStrategy,
            this.conceptEnhancementSessionNameStrategy,
        ];
    }

    public convert = (
        session: AiTutorSessionDto,
        curriculumDtos: CurriculumDto[],
        middleUnitDtos: MiddleUnitDto[],
    ): SessionItemProps => {
        const sessionItemProps: SessionItemProps = {
            id: session.id,
            selected: false,
            sessionType: "기본학습",
            sessionName: "",
            sessionProblemId: session.metadata?.problemId ?? 0,
            createdAt: session.createdAt
                ? this.dateAdapter.formatRelativeDateIn24Hours(
                      new Date(session.createdAt),
                      "Asia/Seoul",
                  )
                : "-",
            originCreatedAt: session.createdAt
                ? this.dateAdapter.parseISO(session.createdAt)
                : null,
        };

        // 세션 이름 : {대단원} - {학습 섹션} - {문항 번호}
        // 일단 sudo code 작성
        const source = session.source;

        for (const strategy of this.sessionNameStrategies) {
            if (strategy.canHandle(source)) {
                const { sessionName, sessionType } = strategy.build(source, {
                    curriculumDtos,
                    middleUnitDtos,
                });
                sessionItemProps.sessionName = sessionName;
                sessionItemProps.sessionType = sessionType;
                break;
            }
        }

        return sessionItemProps;
    };
}
